import { Inject, Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { Platform } from '@ionic/angular';
import * as SentryAngular from '@sentry/angular';
import { take } from 'rxjs/operators';
import { Branch, EnvironmentService } from 'src/app/shared/services/environment/environment.service';
import { LogLevel } from '../contracts/logging/log-level';
import { ILogger } from '../contracts/logging/logger';
import { IStateRegistry } from '../contracts/state/state-registry';
import { StateRegistry } from '../state/state-registry';

@Injectable({ providedIn: 'root' })
export class Logger implements ILogger {
  private static instance: ILogger;
  private _logLevel: LogLevel;

  get logLevel(): LogLevel {
    return this._logLevel;
  }

  set logLevel(logLevel: LogLevel) {
    this._logLevel = logLevel;
  }

  constructor(
    @Inject(StateRegistry) private _stateRegistry: IStateRegistry,
    private _platform: Platform,
    private _environmentService: EnvironmentService
  ) {
    this._stateRegistry.update('sentry', 'context', {});
    // the user and the session tags are the SessionTelemetry's job; they change while the app runs
    this.setDeviceScope();
    this._logLevel = LogLevel.verbose;

    Logger.setInstance(this);
  }

  private static setInstance(logger: ILogger) {
    Logger.instance = logger;
  }

  static log(message: string, level: LogLevel = LogLevel.verbose) {
    return Logger.instance.log(message, level);
  }

  static error(message: string, error: Error) {
    return Logger.instance.error(message, error);
  }

  static info(message: string, level: LogLevel = LogLevel.info) {
    return Logger.instance.info(message, level);
  }

  log(message: string, level: LogLevel = LogLevel.verbose) {
    console.log(message);
  }

  error(message: string, error: Error, level: LogLevel = LogLevel.fatal) {
    console.error(`Error occured / message: ${message}`, error);
    if (this._environmentService.branch === Branch.dev || level === LogLevel.silent) {
      return;
    }

    this._stateRegistry
      .select('sentry', 'context')
      .pipe(take(1))
      .subscribe((context: any) => {
        if (context) {
          SentryAngular.withScope(scope => {
            scope.addBreadcrumb({ data: context, level: 'info' });
            if (error) {
              SentryAngular.captureException(error);
            } else {
              SentryAngular.captureMessage(message);
            }
          });
          return;
        }
        if (error) {
          SentryAngular.captureException(error);
        } else {
          SentryAngular.captureMessage(message);
        }
      });
  }

  /**
   * Capture error messages in specific parts of the app.
   * Use tags and level to help classifing errors.
   * You can pass message or error
   * @param message The message to capture
   * @param error The error to captupre
   * @param tags Map of tag name to tag value
   * @param level The logging level (see sentry levels)
   * @param extras Arbitrary extras you want to save
   * @param fingerprint Overrides Sentry's grouping. Pass stable, low-cardinality parts only
   *                    (never ids) so that one root cause becomes exactly one issue.
   */
  public captureErrorWithExtras(
    message: string | null,
    error: Error | null,
    tags: Map<string, string> = new Map(),
    level: SentryAngular.SeverityLevel = 'info',
    extras: Record<string, unknown> = {},
    fingerprint?: string[]
  ): void {
    console.error(message, error, tags, level, extras);
    SentryAngular.withScope(scope => {
      scope.setExtras(extras);
      // Map.forEach yields (value, key) — naming the parameters the other way round swapped
      // tag name and value in Sentry, which made every tag unusable for filtering.
      tags.forEach((tagValue, tagName) => scope.setTag(tagName, tagValue));
      scope.setLevel(level);
      if (fingerprint?.length) {
        scope.setFingerprint(fingerprint);
      }
      if (error) {
        SentryAngular.captureException(error);
      } else {
        SentryAngular.captureMessage(message);
      }
    });
  }

  public addBreadcrumb(breadcrumb: SentryAngular.Breadcrumb): void {
    SentryAngular.addBreadcrumb(breadcrumb);
  }

  public setTag(name: string, value: string): void {
    SentryAngular.setTag(name, value);
  }

  public setContext(name: string, data: Record<string, unknown> | null): void {
    SentryAngular.setContext(name, data);
  }

  public setUser(user: { id?: string; email?: string; username?: string } | null): void {
    SentryAngular.setUser(user);
  }

  info(message: string, level: LogLevel = LogLevel.info) {
    console.log(message);
  }

  private setDeviceScope(): void {
    if (!this._platform.is('hybrid')) {
      return;
    }
    const scope = SentryAngular.getCurrentScope();
    Device.getInfo().then(deviceinfo => {
      scope.setExtras({
        manufacturer: deviceinfo.manufacturer,
        model: deviceinfo.model,
        os: deviceinfo.platform,
        os_version: deviceinfo.osVersion,
        build_number: this._environmentService.buildNumber,
      });
    });
  }
}
